<template>
  <div
    id="register"
    class="authentication-container"
    :class="{'authentication-container-mobile': isMobileSize}"
  >

    <!-- LEFT SIDE -->

    <left-side v-if="!isMobileSize"/>

    <!-- RIGHT SIDE -->

    <div class="right-side" :class="{'mobile': isMobileSize}">

      <!-- TITLE -->

      <div class="title">
        <h3 class="title-header">Sign up</h3>
      </div>

      <!-- INPUTS -->

      <div class="content form" :class="{'mobile': isMobileSize}">
        <div class="inputs">
          <div class="input-container" :class="{'mobile': isMobileSize}">
            <label
              for="title"
              class="label"
            >User ID</label>
            <input
              type="text"
              id="title"
              class="primary-input"
              v-model.trim="$v.credentials.title.$model"
              :disabled="googleToken"
              :class="[$v.credentials.title.$error ? 'error' : null,
                            $v.credentials.title.$dirty &&
                            !$v.credentials.title.$invalid ? 'success' : null]"
            >
            <validation-message
              :validation="$v.credentials.title"
              :metadata="memberMetadata.fields.title"
            />
          </div>

          <div class="input-container" :class="{'mobile': isMobileSize}">
            <label
              for="firstName"
              class="label"
            >First Name</label>
            <input
              type="text"
              id="firstName"
              class="primary-input"
              v-model.trim="$v.credentials.firstName.$model"
              :disabled="googleToken"
              :class="[$v.credentials.firstName.$error ? 'error' : null,
                            $v.credentials.firstName.$dirty &&
                            !$v.credentials.firstName.$invalid ? 'success' : null]"
            >
            <validation-message
              :validation="$v.credentials.firstName"
              :metadata="memberMetadata.fields.firstName"
            />
          </div>

          <div class="input-container" :class="{'mobile': isMobileSize}">
            <label
              for="lastName"
              class="label"
            >{{ memberMetadata.fields.lastName.label }}</label>
            <input
              type="text"
              id="lastName"
              class="primary-input"
              v-model.trim="$v.credentials.lastName.$model"
              :disabled="googleToken"
              :class="[$v.credentials.lastName.$error ? 'error' : null,
                            $v.credentials.lastName.$dirty &&
                            !$v.credentials.lastName.$invalid ? 'success' : null]"
            >
            <validation-message
              :validation="$v.credentials.lastName"
              :metadata="memberMetadata.fields.lastName"
            />
          </div>

          <div class="input-container" :class="{'mobile': isMobileSize}">
            <label
              for="password"
              class="label"
            >{{ memberMetadata.fields.password.label }}</label>
            <input
              type="password"
              id="password"
              class="primary-input"
              v-model="password"
              :disabled="googleToken"
              :class="[$v.credentials.password.$error ? 'error' : null,
                            $v.credentials.password.$dirty &&
                            !$v.credentials.password.$invalid ? 'success' : null]"
            >
            <password-validation-message
              :validation="$v.credentials.password"
              :metadata="memberMetadata.fields.password"
              :newPassword="true"
            />
          </div>

          <div class="input-container" :class="{'mobile': isMobileSize}">
            <label
              for="confirmPassword"
              class="label"
            >Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              class="primary-input"
              v-model="$v.credentials.confirmPassword.$model"
              :disabled="googleToken"
              :class="[$v.credentials.confirmPassword.$error ? 'error' : null,
                            $v.credentials.confirmPassword.$dirty &&
                            !$v.credentials.confirmPassword.$invalid ? 'success' : null]"
            >
            <password-validation-message
              :validation="$v.credentials.confirmPassword"
              :confirmPassword="true"
            />
          </div>

          <!-- CHECKBOX -->

          <div class="checkbox-container">
            <input
              class="checkbox"
              id="privacyPolicy"
              type="checkbox"
              v-model="termsCheck"
            >
            <label
              class="check"
              for="privacyPolicy"
            ></label>
            <label
              class="label"
              for="privacyPolicy"
            >By signing up, you agree to our
              <router-link
                class="link"
                to=""
              >Terms & Conditions</router-link>
            </label>
          </div>
        </div>

        <!-- ACTIONS -->

        <div class="actions">
          <CoreButton
            @click.prevent="register"
            :disabled="isDisabled"
            :loading="isLoading"
          >
            Sign up
          </CoreButton>

          <CoreButton @click="registerWithGoogle" class="login-google">
            Register with Google
            <img
              src="@/assets/google-icon.png"
              alt="alt"
              width="18px"
            >
          </CoreButton>
        </div>
      </div>

      <!-- GO TO LOGIN -->

      <div class="go-to-login">
        <p>Already have an account?
          <router-link
            :to="{path: '/login', query: getQueries()}"
            class="link"
          >Log in</router-link>
        </p>
      </div>
    </div>

  </div>
</template>

<script>
import server, { createValidator } from './../server'
import { sameAs } from 'vuelidate/lib/validators'
import { mapMutations } from 'vuex'
import { mixin as clickout } from 'vue-clickout'
import { PandaService } from '../utils/request'
import { createToastNotification } from '../utils/toast'
import { GOOGLE_CLIENT_ID, GOOGLE_OAUTH_URL } from '../settings'
import { WaitStates } from '../utils/vuewait'

const CoreButton = () => import(
  /* webpackChunkName: "CoreButton" */ './../components/Core/CoreButton.vue'
)
const ValidationMessage = () => import(
  /* webpackChunkName: "ValidationMessage" */ './../components/ValidationMessage'
)
const PasswordValidationMessage = () => import(
  /* webpackChunkName: "PasswordValidationMessage" */ './../components/PasswordValidationMessage'
)
const LeftSide = () => import(
  /* webpackChunkName: "LeftSide" */ './../components/LeftSide'
)

export default {
  mixins: [clickout],
  name: 'Register',
  data () {
    return {
      password: null,
      credentials: {
        title: null,
        firstName: null,
        lastName: null,
        password: null,
        confirmPassword: null
      },
      memberMetadata: server.metadata,
      termsCheck: false,
      applicationId: this.$route.query.applicationId,
      state: this.$route.query.state,
      scopes: this.$route.query.scopes,
      token: this.$route.query.t_,
      redirectUri: this.$route.query.redirectUri,
      googleToken: this.$route.query.code,
      googleState: this.$route.query.state
    }
  },
  validations () {
    return {
      credentials: {
        title: createValidator(this.memberMetadata.fields.title),
        firstName: createValidator(this.memberMetadata.fields.firstName),
        lastName: createValidator(this.memberMetadata.fields.lastName),
        password: createValidator(this.memberMetadata.fields.password),
        confirmPassword: {
          sameAs: sameAs(() => this.password)
        }
      }
    }
  },
  computed: {
    query () {
      let queries = {}
      if (this.applicationId) {
        queries.applicationId = this.applicationId
      }
      if (this.state) {
        queries.state = this.state
      }
      if (this.redirectUri) {
        queries.redirectUri = this.redirectUri
      }
      if (this.scopes) {
        queries.scopes = this.scopes
      }
      if (this.token) {
        let member = atob(this.token.split('.')[1])
        let organizationId = JSON.parse(member).organizationId
        queries.organizationId = String(organizationId)
      }
      return queries
    },
    isDisabled () {
      const { title, firstName, lastName, confirmPassword } = this.credentials
      return !this.termsCheck || !this.password || !title || !firstName || !lastName || !confirmPassword
    },
    isLoading () {
      return this.$wait.is(WaitStates.REGISTER_LOADING)
    },
    registerFormData () {
      if (this.googleToken) {
        return {
          oauthCode: this.googleToken,
          ownershipToken: JSON.parse(this.googleState).token,
          redirectUrl: window.location.href.split('?')[0]
        }
      }
      return {
        title: this.credentials.title,
        firstName: this.credentials.firstName,
        lastName: this.credentials.lastName,
        password: this.credentials.confirmPassword,
        ownershipToken: this.token
      }
    },
    isMobileSize () {
      return window.innerWidth <= 768
    }
  },
  methods: {
    registerWithGoogle () {
      window.location.href = `${GOOGLE_OAUTH_URL}?response_type=code&client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${window.location.protocol}//${window.location.hostname}%2Fregister&scope=email+profile&state=${this.stringifyRegisterData()}`
    },
    async register () {
      if (!this.googleToken) {
        this.$v.credentials.password.$model = this.password
        this.$v.credentials.$touch()
        if (this.$v.credentials.$invalid) return
      }

      this.$wait.start(WaitStates.REGISTER_LOADING)
      const { status, statusText, headers } = await PandaService({
        url: 'members',
        method: 'REGISTER',
        data: this.registerFormData
      })
      this.$wait.end(WaitStates.REGISTER_LOADING)

      if (status === 200) {
        createToastNotification(statusText, { type: 'success', position: 'bottom' })
        this.confirmIdentity()
        if (headers['x-new-jwt-token']) {
          server.token = headers['x-new-jwt-token']
        }
        if (this.applicationId) {
          this.$router.push({ path: '/permissions', query: this.query })
        } else {
          this.$router.push('/')
        }
      }
    },
    getQueries () {
      let queries = {}
      for (let query in this.$route.query) {
        queries[query] = this.$route.query[query]
      }
      if (Object.keys(queries).length) {
        return queries
      } else {
        return null
      }
    },
    stringifyRegisterData () {
      const registerData = {
        token: this.token,
        applicationId: this.applicationId,
        state: this.state,
        scopes: this.scopes,
        redirectUri: this.redirectUri
      }

      return JSON.stringify(registerData)
    },
    ...mapMutations([
      'confirmIdentity'
    ])
  },
  async beforeMount () {
    if (this.googleToken) {
      const registerData = JSON.parse(this.googleState)

      this.applicationId = registerData.applicationId
      this.state = registerData.state
      this.scopes = registerData.scopes
      this.token = registerData.token
      this.redirectUri = registerData.redirectUri

      await this.register()
    }
  },
  components: {
    LeftSide,
    ValidationMessage,
    PasswordValidationMessage,
    CoreButton
  }
}
</script>
